import { HistoryOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Space } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import DataObjectsPreviewList from "components/molecules/DataObjectsPreviewList";
import DataObjectRequestVerification from "containers/DataObjectRequestVerification";
import _ from "lodash";
import React, { Component } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataSetBatchUploadHistoryModal from "../DataSetBatchUploadHistoryModal";

type IManageDataSetProps = {
    action: "view" | "edit" | "create";
    permission: {
        canAdd: boolean;
        canEdit: boolean;
    };
    schemaModel: ISchemaModel;
    dataSet: IDataSet;
    dataObjectsResult: {
        pageKey?: string;
    };
    dataObjects: IDataObject[];
    formErrors?: Record<string, string>;
    fetchingBatchUploadJobs?: boolean;
    undoJobInProgress?: boolean;
    jobList: unknown[];
    bulkVerification: boolean;
    loadingDataObjects: boolean;
    loadingNextPageDataObjects: boolean;
    onUpdateDataSet: (
        prevDs: IDataSet,
        newDs: IDataSet,
        callback: (() => void) | null,
    ) => void;
    onCreateDataSet: (newDs: IDataSet, callback: (() => void) | null) => void;
    onBulkUploadData: (name: string, source: unknown) => void;
    onGetNextDataObjects?: (name: string, key: string) => void;
    refreshDataObjects: () => void;
    onGetBatchUploadJobs: (id: string) => void;
    undoJob: (jobId: string, version: string) => void;
    onValuesChanged: (changed: any, existing: IDataSet) => void;
    onCancel: () => void;
    onCreateNewDataObject: () => void;
    onDataObjectSelected: (data: IDataObject) => void;
    onShowDataObjectHistory: (setName: string, dataOBjectId: string) => void;
    onDeleteDataObject: (id: string) => void;
};

type IManageDataSetStates = {
    readOnlyView: boolean;
    afterSaveCallback: (() => void) | null;
    showSaveConfirmationModal: boolean;
    batchUploadModalVisible: boolean;
};

type IGetFormErrors = {
    help: string;
    validateStatus:
        | ""
        | "error"
        | "success"
        | "warning"
        | "validating"
        | undefined;
};

class ManageDataSet extends Component<
    IManageDataSetProps,
    IManageDataSetStates
> {
    private formRef;
    private defaultDataSet;

    constructor(props: IManageDataSetProps) {
        super(props);
        this.state = {
            readOnlyView: this.props.action === "view",
            afterSaveCallback: null,
            batchUploadModalVisible: false,
            showSaveConfirmationModal: false,
        };
        this.defaultDataSet = {
            schemaName: "",
            schemaDescription: "",
            dataSetName: "",
            dataSetDescription: "",
        };
        this.formRef = React.createRef<any>();
    }

    componentDidMount() {
        this.populateDataSet();
    }

    populateDataSet = () => {
        let dataSet = { ...this.defaultDataSet };
        if (this.props.schemaModel) {
            dataSet.schemaName = this.props.schemaModel.name;
            dataSet.schemaDescription = this.props.schemaModel.description;
        }
        if (this.props.dataSet) {
            dataSet.dataSetName = this.props.dataSet.dataSetName;
            dataSet.dataSetDescription = this.props.dataSet.dataSetDescription;
        }

        this.formRef.current.resetFields();
        this.formRef.current.setFieldsValue(dataSet);
        this.defaultDataSet = this.formRef.current.getFieldsValue();
    };

    isReadOnlyView = () => {
        return (
            this.state.readOnlyView ||
            !(this.props.permission.canEdit || this.props.permission.canAdd)
        );
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    };

    onFormSubmit = (dataSet: IDataSet) => {
        if (!this.isSaveAllowed()) {
            return;
        }
        if (this.props.action === "edit") {
            let updatedDataSet = { ...dataSet };
            this.props.onUpdateDataSet(
                this.props.dataSet,
                updatedDataSet,
                this.state.afterSaveCallback,
            );
        } else if (this.props.action === "create") {
            let newDataSet = { ...dataSet };
            this.props.onCreateDataSet(
                newDataSet,
                this.state.afterSaveCallback,
            );
        }
        this.setState({
            afterSaveCallback: null,
        });
    };

    onBulkUploadData = (source: unknown) => {
        let dataSet = this.formRef.current.getFieldsValue();
        if (this.props.action === "create") {
            this.setState({
                afterSaveCallback: () =>
                    this.props.onBulkUploadData(dataSet.dataSetName, source),
                showSaveConfirmationModal: true,
            });
        } else if (this.props.action === "edit") {
            if (_.isEqual(dataSet, this.defaultDataSet) === false) {
                this.setState({
                    afterSaveCallback: () =>
                        this.props.onBulkUploadData(
                            dataSet.dataSetName,
                            source,
                        ),
                    showSaveConfirmationModal: true,
                });
            } else {
                this.props.onBulkUploadData(dataSet.dataSetName, source);
            }
        } else {
            this.props.onBulkUploadData(dataSet.dataSetName, source);
        }
    };

    onSaveConfirmOk = () => {
        this.setState(
            {
                showSaveConfirmationModal: false,
            },
            () => {
                this.formRef.current && this.formRef.current.submit();
            },
        );
    };

    onSaveConfirmCancel = () => {
        this.setState({
            showSaveConfirmationModal: false,
            afterSaveCallback: null,
        });
    };

    getFormErrors = (fieldName: string): IGetFormErrors | {} => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.props.formErrors[fieldName],
                validateStatus: "error",
            };
        }
        return {};
    };

    onDataObjectPageSelect = (pagination: IPagination) => {
        if (
            this.props.onGetNextDataObjects &&
            this.props.dataObjectsResult.pageKey &&
            this.props.dataObjects &&
            pagination.current * pagination.pageSize >=
                this.props.dataObjects.length
        ) {
            this.props.onGetNextDataObjects(
                this.props.dataSet.dataSetId,
                this.props.dataObjectsResult.pageKey,
            );
        }
    };

    showBatchUploadJobsModal = () => {
        this.setState({
            batchUploadModalVisible: true,
        });
        this.props.onGetBatchUploadJobs(this.props.dataSet.dataSetId);
    };

    closeBatchUploadJobsModal = (jobUndoInitiated: boolean) => {
        this.setState({
            batchUploadModalVisible: false,
        });
        if (jobUndoInitiated) {
            this.props.refreshDataObjects();
        }
    };

    render() {
        return (
            <>
                {this.state.batchUploadModalVisible && (
                    <DataSetBatchUploadHistoryModal
                        visible={this.state.batchUploadModalVisible}
                        loadingData={this.props.fetchingBatchUploadJobs}
                        isBusy={this.props.undoJobInProgress}
                        jobList={this.props.jobList}
                        onCancel={this.closeBatchUploadJobsModal}
                        onOk={this.closeBatchUploadJobsModal}
                        onUndoJob={this.props.undoJob}
                    ></DataSetBatchUploadHistoryModal>
                )}
                <Modal
                    title="Save Dataset"
                    centered={true}
                    open={this.state.showSaveConfirmationModal}
                    onOk={this.onSaveConfirmOk}
                    onCancel={this.onSaveConfirmCancel}
                >
                    <p>
                        There are unsaved changes. Do you want to save dataset?
                    </p>
                </Modal>
                <FlexContainerLayout
                    header={
                        <Form
                            ref={this.formRef}
                            name="basic"
                            layout="vertical"
                            onFinish={this.onFormSubmit}
                            initialValues={this.defaultDataSet}
                            onValuesChange={this.props.onValuesChanged}
                            className="flex w-full flex-row gap-4"
                        >
                            <div className="flex w-full flex-1 flex-col gap-4">
                                <div className="flex w-full flex-row justify-stretch gap-x-4">
                                    <Form.Item
                                        shouldUpdate={true}
                                        className="mb-0 flex-1"
                                    >
                                        {() => {
                                            return (
                                                this.formRef &&
                                                this.formRef.current && (
                                                    <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                                                        <p className="text-sm font-bold text-rose-800">
                                                            Schema Name
                                                        </p>
                                                        <p className="font-bold">
                                                            {this.formRef.current.getFieldValue(
                                                                ["schemaName"],
                                                            )}
                                                        </p>
                                                    </div>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                    <Form.Item
                                        shouldUpdate={true}
                                        className="mb-0 flex-1"
                                    >
                                        {() => {
                                            return (
                                                this.formRef &&
                                                this.formRef.current && (
                                                    <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                                                        <p className="text-sm font-bold text-sky-800">
                                                            Schema Description
                                                        </p>
                                                        <p className="font-bold">
                                                            {this.formRef.current.getFieldValue(
                                                                [
                                                                    "schemaDescription",
                                                                ],
                                                            )}
                                                        </p>
                                                    </div>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                                <div className="flex w-full flex-row gap-x-4">
                                    <Form.Item
                                        {...this.getFormErrors("dataSetName")}
                                        label="Dataset Name"
                                        name={["dataSetName"]}
                                        validateFirst={true}
                                        rules={nameRules}
                                        className="mb-0 flex-1"
                                    >
                                        <Input
                                            readOnly={this.isReadOnlyView()}
                                            data-testid={
                                                CypressTestIds.DATASET_MANAGE_DATASET_NAME_INPUT
                                            }
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        {...this.getFormErrors(
                                            "dataSetDescription",
                                        )}
                                        label="Dataset Description"
                                        name={["dataSetDescription"]}
                                        rules={descriptionRules}
                                        className="mb-0 flex-1"
                                    >
                                        <Input
                                            readOnly={this.isReadOnlyView()}
                                            data-testid={
                                                CypressTestIds.DATASET_MANAGE_DATASET_DESCRIPTION_INPUT
                                            }
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div>
                                <Space direction="vertical">
                                    <Button
                                        style={{ width: "100%" }}
                                        onClick={() =>
                                            this.onBulkUploadData(
                                                "systemconnection",
                                            )
                                        }
                                        data-testid={
                                            CypressTestIds.DATASET_MANAGE_DATASET_CONNECT_TO_A_SYSTEM_BUTTON
                                        }
                                    >
                                        Connect to a System
                                    </Button>
                                    {this.props.dataObjects &&
                                        this.props.dataObjects.length > 0 && (
                                            <DataObjectRequestVerification
                                                disabled={
                                                    !this.props
                                                        .bulkVerification ||
                                                    !this.props.schemaModel ||
                                                    !this.props.schemaModel
                                                        .fields ||
                                                    this.props.schemaModel
                                                        .fields.length === 0 ||
                                                    this.props
                                                        .loadingDataObjects ||
                                                    !this.props.dataObjects ||
                                                    this.props.dataObjects
                                                        .length === 0
                                                }
                                                key={
                                                    this.props.dataObjects[0]
                                                        .dataObjectId
                                                }
                                                dataObject={
                                                    this.props.dataObjects[0]
                                                }
                                                schemaFields={
                                                    (this.props.schemaModel &&
                                                        this.props.schemaModel
                                                            .fields) ||
                                                    []
                                                }
                                                bulkVerification={
                                                    this.props.bulkVerification
                                                }
                                            />
                                        )}
                                    <Button
                                        style={{ width: "100%" }}
                                        type="primary"
                                        onClick={() =>
                                            this.onBulkUploadData("file")
                                        }
                                        data-testid={
                                            CypressTestIds.DATASET_MANAGE_DATASET_LOAD_DATA_FILE_INPUT
                                        }
                                    >
                                        Load data from a file
                                    </Button>
                                </Space>
                            </div>
                        </Form>
                    }
                    footer={
                        <div className="flex w-full flex-row gap-3">
                            <Button
                                onClick={this.showBatchUploadJobsModal}
                                disabled={this.props.action === "create"}
                                type="link"
                                className="border border-slate-100 bg-slate-50 hover:bg-gray-50"
                            >
                                Batch Upload History <HistoryOutlined />
                            </Button>
                            <div className="flex-1" />
                            <Button onClick={this.props.onCancel}>
                                Cancel
                            </Button>
                            {this.props.action === "edit" && (
                                <Button
                                    disabled={
                                        this.isReadOnlyView() ||
                                        !this.isSaveAllowed()
                                    }
                                    type="primary"
                                    onClick={this.props.onCreateNewDataObject}
                                    data-testid={
                                        CypressTestIds.DATASET_MANAGE_DATASET_CANCEL_BUTTON
                                    }
                                >
                                    <PlusOutlined /> Add Data
                                </Button>
                            )}
                            <Button
                                disabled={
                                    this.isReadOnlyView() ||
                                    !this.isSaveAllowed()
                                }
                                onClick={() =>
                                    this.formRef.current &&
                                    this.formRef.current.submit()
                                }
                                htmlType="submit"
                                type="primary"
                                data-testid={
                                    CypressTestIds.DATASET_MANAGE_DATASET_SAVE_BUTTON
                                }
                            >
                                Save
                            </Button>
                            <Button
                                type="primary"
                                disabled={true}
                                data-testid={
                                    CypressTestIds.DATASET_MANAGE_DATASET_EXPORT_BUTTON
                                }
                            >
                                Export
                            </Button>
                        </div>
                    }
                >
                    <DataObjectsPreviewList
                        showHeader={false}
                        schemaFields={
                            (this.props.schemaModel &&
                                this.props.schemaModel.fields) ||
                            []
                        }
                        dataObjectList={
                            this.props.loadingDataObjects
                                ? []
                                : this.props.dataObjects
                        }
                        loading={
                            this.props.loadingDataObjects ||
                            this.props.loadingNextPageDataObjects
                        }
                        onPageSelect={this.onDataObjectPageSelect}
                        onDataObjectSelected={this.props.onDataObjectSelected}
                        onShowDataObjectHistory={(dataObjectId: string) =>
                            this.props.onShowDataObjectHistory(
                                this.props.dataSet.dataSetName,
                                dataObjectId,
                            )
                        }
                        onDeleteDataObject={this.props.onDeleteDataObject}
                        recordCount={this.props.dataObjects?.length ?? 0}
                    />
                </FlexContainerLayout>
            </>
        );
    }
}

export default ManageDataSet;
